import {shallowRef} from "vue"
import {RouteLocationRaw} from "vue-router"
import {EnhancedWorkpace} from "@/types/Workspace"
import {InstanceContext} from "@/types/InstanceContext"
import Idea from "@/types/Idea"
import IdeaService from "@/services/IdeaService"

/**
 * Pode criar nesta coleção? O nível e o ambiente aberto (`can('editor')`), e o `creatable` quando ele
 * não depende do item (`_creatable`, que o servidor avalia para quem olha). Dependendo do item, o
 * botão aparece e o salvar decide.
 *
 * Função de módulo, e não só método das telas: a barra do casco também oferece criar, e ela não é
 * uma rota. Um dono para as duas, senão o "+" do topo e o "Criar" da lista discordariam.
 */
export function canCreateIn(workspace: EnhancedWorkpace, idea: Idea | undefined): boolean {
    return workspace.can('editor') && !(idea && idea._creatable === false)
}

/**
 * A página de criar um item nesta coleção. É para onde vão o "+" da barra do app e o "+" das abas
 * (decisão do usuário: na barra do app, rota; na faixa do caminho, janela) e o "abrir em página
 * inteira" das janelas de criar.
 */
export function createRoute(system: string, ideaAlias: string): RouteLocationRaw {
    return {name: 'create-instance', params: {system, idea: ideaAlias}}
}

/**
 * O pedido de criar um item numa JANELA, por cima da tela atual: o "+" da faixa do caminho, o
 * "Criar" da lista no celular (o espelho dele), o da página do ambiente. Quem ATENDE é uma janela só,
 * no `App.vue`, fora das telas — a de criar é a da ficha, aberta já criando, que ao salvar vira a
 * ficha do item.
 *
 * Estado de módulo, como o usuário do `storage.ts`, e não eventos: as portas estão na barra e nas
 * telas, e a janela no casco. Por evento seria uma corrente por porta, subindo até o casco.
 *
 * O `known` vai junto do pedido, tirado do que a tela aberta OFERECEU para a coleção (ver o
 * `offerKnown`). Sem oferta, null, e o formulário busca.
 */
export const creationRequest = shallowRef<{ idea: string, known: any } | null>(null)

export function requestCreation(ideaAlias: string) {
    creationRequest.value = {idea: ideaAlias, known: knownOffered(ideaAlias)}
}

/**
 * O que as telas abertas já leram para o formulário de criar, por coleção (ver o `knownForCreate`).
 *
 * A lista e o quadro leem a coleção com os candidatos de cada relacionamento para se desenhar, e com
 * isso o formulário abria sem ir ao servidor. Enquanto o "Criar" era delas, entregavam direto; o "+"
 * da faixa do caminho mora no casco e não enxerga a tela — e a janela passou a abrir com carregando,
 * buscando o que estava na tela (relato do usuário). Agora a tela OFERECE, e qualquer porta aproveita.
 *
 * Uma função, e não o valor: os candidatos chegam depois da montagem, e o que vale é o que a tela
 * tem no instante do pedido. Devolve quem retira a oferta, a chamar ao desmontar.
 *
 * Uma PILHA por coleção, e não uma oferta só: a ficha de um sistema aberta por cima da lista de
 * sistemas também oferece, e ao fechar ela retiraria a única que havia, deixando a lista sem a sua.
 * Vale a mais recente que já tenha o que dar — a ficha ainda na prévia não tem, e a lista embaixo
 * responde por ela.
 */
const offered = new Map<string, Array<() => any>>()

export function offerKnown(ideaAlias: string, known: () => any): () => void {
    offered.set(ideaAlias, [...(offered.get(ideaAlias) || []), known])
    return () => offered.set(ideaAlias, (offered.get(ideaAlias) || []).filter(other => other !== known))
}

function knownOffered(ideaAlias: string): any {
    for (const known of [...(offered.get(ideaAlias) || [])].reverse()) {
        const value = known()
        if (value) return value
    }
    return null
}

/**
 * O último item criado pela janela do casco — para quem MOSTRA a coleção se atualizar: a lista e o
 * quadro abertos atrás dela, a contagem na página do ambiente. O `at` faz dois itens seguidos da
 * mesma coleção contarem como duas mudanças.
 */
export const lastCreated = shallowRef<{ idea: string, instance: number, at: number } | null>(null)

export function noteCreated(ideaAlias: string, instance: number) {
    lastCreated.value = {idea: ideaAlias, instance, at: Date.now()}
}

/**
 * O que o formulário de criar leria, montado do que a tela JÁ TEM — ou null, quando ela não tem.
 *
 * Abrir o formulário custava duas coisas: a coleção com o fluxo e os domínios, e os candidatos
 * de cada relacionamento (`rel-domains`), que é uma consulta por coleção apontada. Uma tela de
 * coleção já pagou a segunda para desenhar o que mostra, e a primeira está no ambiente desde o
 * começo. Entregando as duas, o modal abre sem ir ao servidor.
 *
 * **Mora aqui porque a lista e o quadro fazem a mesma coisa**: são duas visões da mesma coleção,
 * e a montagem é idêntica — só o nome do campo onde cada uma guardou os candidatos difere.
 *
 * **Null quando não há candidatos lidos**, e esta é a parte que exige cuidado: entregar um mapa
 * vazio diria "esta coleção não tem candidato nenhum", e o formulário aceitaria a mentira em vez
 * de ir buscar. Por isso quem chama passa `null` — e não `{}` — enquanto não leu.
 */
/**
 * A chave da entrega de um rascunho de criação: a COLEÇÃO, porque ainda não há item.
 *
 * Própria, e não o `instanceKey` com o id vazio: "sistema/colecao/undefined" casaria com
 * qualquer outra tela sem item da mesma coleção.
 */
export function createDraftKey(context: InstanceContext): string {
    return `create:${context.system}/${context.idea}`
}

/**
 * O que o formulário de criar leria, lido do servidor, na forma do `knownForCreate`: para quem não
 * encontrou nada na tela.
 *
 * `meta: workflow` porque sem o fluxo resolvido o `consistantInstance` descarta o `_state` e o item
 * nasceria sempre na situação inicial — o que tornaria inócuo tanto o "+" de uma faixa quanto o campo
 * de situação. `meta: domains` porque o formulário abre de dentro de outros e não conhece o ambiente.
 *
 * Dois chamadores: o formulário, quando a janela que o abriu não tinha o que dar, e a página de criar,
 * que lê ANTES de montá-lo. Na página quem carrega é ela, com a barra no alto como as outras rotas: o
 * formulário lendo por conta própria mostrava o giro redondo das janelas (relato do usuário).
 */
export async function readForCreate(ideas: IdeaService, systemAlias: string, ideaAlias: string): Promise<any> {
    const res = await ideas.get({systemAlias, ideaAlias}, {options: ["rel-domains"], meta: ["workflow", "domains"]})
    return {idea: res.value, domains: res.relDomains || {}, reverseInfo: res.reverseInfo}
}

export function knownForCreate(
    workspace: EnhancedWorkpace, ideaAlias: string, relDomains: any, reverseInfo: any
): any {
    if (!relDomains) return null
    return {
        idea: workspace.resolvedIdeaWith(ideaAlias),
        domains: relDomains,
        reverseInfo
    }
}
